import { useRef, useState } from 'react';
import { Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

import { cn } from '../../lib/cn';

/**
 * `Tabs` của antd, thêm hiệu ứng **trượt qua lại** giữa các thẻ (ADR-UI-0008).
 *
 * antd đổi nội dung thẻ tức thì, không có chuyển tiếp nào. Component này giữ
 * nguyên toàn bộ phần đầu thẻ, bàn phím và trợ năng của antd — chỉ bọc **phần
 * nội dung** vào `motion` để nó trượt.
 *
 * Hướng trượt theo **thứ tự thẻ**, không phải theo thời gian: bấm sang thẻ bên
 * phải thì nội dung mới vào từ phải, bấm ngược lại thì vào từ trái. Nhờ vậy
 * hiệu ứng khớp với mô hình không gian mà người dùng thấy ở hàng thẻ.
 *
 * Cách làm: đưa `items` cho antd nhưng **bỏ `children`**, nên antd chỉ vẽ hàng
 * thẻ; nội dung do component này tự vẽ bên dưới. Nếu để antd vẽ cả nội dung
 * thì nó vẫn đổi tức thì và `AnimatePresence` không thấy gì để chuyển tiếp.
 *
 * Hàng thẻ nằm trong khung `surface` có viền như một thẻ nội dung, thẻ đang mở
 * tô nền nhạt màu nhấn (lớp `tabs-truot` ở `globals.css`): trên nền trang xám,
 * một hàng chữ trần với gạch chân mảnh thì chìm và khó biết đang ở thẻ nào.
 */

/** Trượt 180ms — nhanh hơn chuyển trang (300ms) vì đổi thẻ là thao tác nhỏ hơn. */
const THOI_LUONG = 0.18;
/** Quãng trượt. Giữ ngắn: đi xa trông như trang mới, không phải thẻ khác. */
const QUANG = 24;

const EASE_OUT = [0.16, 1, 0.3, 1] as const;

export interface TabsTruotProps extends Omit<TabsProps, 'activeKey' | 'onChange'> {
  items: NonNullable<TabsProps['items']>;
  /** Thẻ mở sẵn. Bỏ trống thì lấy thẻ đầu tiên. */
  defaultActiveKey?: string;
  onChange?: (key: string) => void;
  /**
   * Bỏ khung của hàng thẻ khi `TabsTruot` đã nằm trong một thẻ nội dung (khung
   * lồng khung trông như hai tầng). Thẻ đang mở vẫn tô nền màu nhấn.
   */
  gon?: boolean;
  /**
   * Hết chỗ thì thẻ xuống dòng thay vì dồn vào menu "…" của antd — menu đó giấu luôn thẻ đang mở, người dùng
   * không biết mình đang ở đâu. Dùng cho hàng thẻ ngắn (vài thẻ) có nhãn dài, nhất là ở 768px.
   */
  xuongDong?: boolean;
}

export function TabsTruot({ items, defaultActiveKey, onChange, gon = false, xuongDong = false, ...props }: TabsTruotProps) {
  const [key, setKey] = useState(defaultActiveKey ?? String(items[0]?.key ?? ''));
  /* Giữ hướng của lần đổi gần nhất để `exit` và `initial` cùng chiều. */
  const huong = useRef(1);
  const giamChuyenDong = useReducedMotion();

  const dangMo = items.find((t) => String(t.key) === key) ?? items[0];

  const doiThe = (keyMoi: string) => {
    const cu = items.findIndex((t) => String(t.key) === key);
    const moi = items.findIndex((t) => String(t.key) === keyMoi);
    huong.current = moi > cu ? 1 : -1;
    setKey(keyMoi);
    onChange?.(keyMoi);
  };

  return (
    <>
      {/*
        `items` bỏ `children`: antd chỉ vẽ hàng thẻ. Vẫn truyền đủ `key`,
        `label`, `disabled`… nên `role="tab"`, `aria-selected`, `tabindex` và
        nhãn đọc màn hình (`Tab 1 of 3`) của antd giữ nguyên.

        ⚠️ antd 6 **không** gắn mũi trái/phải để nhảy giữa các thẻ — đã kiểm và
        `Tabs` trần cũng vậy, nên đây không phải hồi quy do lớp bọc này. Thêm
        phím mũi là đầu việc riêng (và phải làm ở `Tabs`, không phải ở đây).
      */}
      <Tabs
        {...props}
        className={cn('tabs-truot', gon && 'tabs-truot-gon', xuongDong && 'tabs-truot-xuong-dong', props.className)}
        activeKey={key}
        onChange={doiThe}
        items={items.map(({ children: _bo, ...the }) => the)}
      />

      {giamChuyenDong ? (
        <div>{dangMo?.children}</div>
      ) : (
        /*
          `mode="wait"` như chuyển trang: hai nội dung cùng lúc sẽ đẩy nhau theo
          chiều dọc, vì `<main>` là khối cuộn duy nhất (§18.3 mục 6).
        */
        <AnimatePresence mode="wait" initial={false} custom={huong.current}>
          <motion.div
            key={key}
            /*
             * `custom` đưa hướng vào các variant. Phải dùng variant chứ không
             * viết thẳng số: với `mode="wait"`, lúc antd đã đổi `key` thì
             * `huong.current` đã là hướng **mới**, nên nếu `exit` đọc trực tiếp
             * biến đó thì nội dung cũ trượt cùng chiều với nội dung mới — trông
             * như cả hai cùng đi một hướng, không còn cảm giác qua lại.
             *
             * `AnimatePresence` giữ lại `custom` tại thời điểm phần tử bị loại,
             * nên variant `exit` nhận đúng hướng của lần đổi đó.
             */
            custom={huong.current}
            variants={{
              /* Vào từ phía người dùng đang đi tới. */
              initial: (h: number) => ({ opacity: 0, x: h * QUANG }),
              animate: {
                opacity: 1,
                x: 0,
                transition: { duration: THOI_LUONG, ease: EASE_OUT },
              },
              /* Ra về phía đối diện, nên hai nội dung luôn ngược chiều nhau. */
              exit: (h: number) => ({
                opacity: 0,
                x: h * -QUANG,
                transition: { duration: THOI_LUONG * 0.6, ease: EASE_OUT },
              }),
            }}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {dangMo?.children}
          </motion.div>
        </AnimatePresence>
      )}
    </>
  );
}
